
// src/components/TrailerSelectorModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Ban,
  Timer,
  AlertTriangle,
  Navigation,
  MapPin,
  Calendar,
  Clock,
  Container,
} from "lucide-react";
import axios from "../axiosConfig";
import "../styles/TrailerSelectorModal.css";
import { useTranslation } from "react-i18next";

function TrailerSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedType, // "pickup" | "delivery"   ✅ (bulk)
  selectedPickupDelivery,
  currentLoad,
  title,
  mode = "single", // "single" | "bulk"
  allPickups = [],
  allDeliveries = [],
}) {

  const quickMode = currentLoad?.isExternalCarrier === true;
  console.log("TrailerSelectorModal currentLoad:", currentLoad);
  console.log("carrierId:", currentLoad?.carrierId, "quickMode:", quickMode);
  const { t, i18n } = useTranslation("trailer_selector_modal"); // (podes trocar mais tarde)
  const locale = useMemo(() => {
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    if (lng.startsWith("fr")) return "fr-FR";
    return "en-GB";
  }, [i18n.language]);

  const [availableTrailers, setAvailableTrailers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loadingTrailers, setLoadingTrailers] = useState(false);
  const [manualPlate, setManualPlate] = useState("");
  const [manualPlateError, setManualPlateError] = useState("");
  // ===== "fun loader" (5 checkpoints) =====
  const LOADING_STEPS = 5;
  const [prepStep, setPrepStep] = useState(0); // 0..4
  const [showPrep, setShowPrep] = useState(false);
  
  const toggleExpand = (id, e) => {
    e?.stopPropagation?.();
    setExpanded((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };
  const trPointType = (rawType) => {
    const key = `pointTypes.${String(rawType || "").toUpperCase()}`;
    const translated = t(key, { defaultValue: "" });
    return translated || rawType || "-";
  };
  const fmtDateTime = (d) => {
    try {
      return d.toLocaleString(locale, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d?.toString?.() ?? "";
    }
  };
  function getTrailerLabel(tr) {
    return tr?.trailerPlate || tr?.trailerNumber || tr?.plate || `#${tr?.id ?? "-"}`;
  }
  function getUniformAssignedTrailerId(points = []) {
  const ids = points
    .map((p) => p?.trailerId ?? null)
    .filter((id) => id != null);
  if (ids.length === 0) return null;
  return ids.every((id) => String(id) === String(ids[0])) ? ids[0] : null;
}
  function isTrailerBlockedForCurrent(trailer, selectedPoint) {
    if (!trailer?.sessionBlocks?.length || !selectedPoint) return null;
    if (!selectedPoint.date || !selectedPoint.hour) return null;
    const sessionId = selectedPoint.transportsessionId ?? null;
    const pointStart = new Date(
      `${selectedPoint.date}T${String(selectedPoint.hour).padStart(5, "0")}:00`
    );
    const pointEnd = new Date(pointStart.getTime() + 45 * 60 * 1000);
    for (const block of trailer.sessionBlocks) {
      const start = new Date(block.start);
      const end = new Date(block.end);
      const blockSessionId = block.transportsessionId ?? block.sessionId ?? null;
      const isSameSession =
        blockSessionId != null &&
        sessionId != null &&
        String(blockSessionId) === String(sessionId);
      // ✅ mesma regra do TruckSelectorModal: ignorar conflitos da MESMA session em single
      if (mode === "single" && isSameSession) continue;
      const overlaps = pointStart < end && start < pointEnd;
      if (overlaps) {
        return {
          isBlocked: true,
          block: {
            transportsessionId: blockSessionId,
            loadId: block.loadId ?? null,
            blockType: block.blockType,
            start,
            end,
            points: block.points || [],
          },
        };
      }
    }
    return null;
  }

  function isTrailerBlockedForBulk(trailer, allPickupsArr = [], allDeliveriesArr = []) {
  if (!trailer?.sessionBlocks?.length) return null;
  const relevantPoints = selectedType === "pickup" ? allPickupsArr : allDeliveriesArr;
  const allPoints = relevantPoints.filter((p) => p?.date && p?.hour);
  const result = [];
  for (const point of allPoints) {
    const pointStart = new Date(
      `${point.date}T${String(point.hour).padStart(5, "0")}:00`
    );
    const pointEnd = new Date(pointStart.getTime() + 45 * 60 * 1000);
    for (const block of trailer.sessionBlocks) {
      if (
        mode === "bulk" &&
        block.transportsessionId &&
        currentLoad?.transportsessionId &&
        String(block.transportsessionId) === String(currentLoad.transportsessionId)
      ) {
        continue;
      }
      const start = new Date(block.start);
      const end = new Date(block.end);
      const isOverlap = pointStart < end && start < pointEnd;
      if (isOverlap) {
        const existing = result.find(
          (b) =>
            b.blockType === block.blockType &&
            (block.blockType === "LOAD_FULL"
              ? b.loadId === block.loadId
              : b.sessionId === block.transportsessionId)
        );
        const cause = {
          type: point.type,
          location: point.location,
          date: point.date,
          hour: point.hour,
        };
        if (existing) {
          existing.causedBy.push(cause);
          existing.causedBy.sort(
            (a, b) =>
              new Date(`${a.date}T${a.hour}:00`) - new Date(`${b.date}T${b.hour}:00`)
          );
        } else {
          result.push({
            sessionId: block.transportsessionId,
            loadId: block.loadId ?? null,
            blockType: block.blockType,
            start,
            end,
            points: block.points || [],
            causedBy: [cause],
          });
        }
      }
    }
  }
  if (result.length === 0) return null;
  const seen = new Set();
  const dedup = [];
  for (const block of result) {
    let key;
    if (block.blockType === "FULL_SESSION") key = `FULL_SESSION-${block.sessionId}`;
    else if (block.blockType === "LOAD_FULL" || block.blockType === "LOAD_HALF")
      key = `${block.blockType}-${block.loadId}`;
    else if (block.blockType === "POINT")
      key = `POINT-${block.loadId}-${block.start.toISOString()}`;
    if (!seen.has(key)) {
      seen.add(key);
      dedup.push(block);
    }
  }
  const hasFullSession = dedup.some((b) => b.blockType === "FULL_SESSION");
  const hasFullLoad = dedup.some((b) => b.blockType === "LOAD_FULL");
  let filtered = dedup;
  if (hasFullSession) {
    filtered = dedup.filter((b) => b.blockType === "FULL_SESSION");
  } else if (hasFullLoad) {
    filtered = dedup.filter(
      (b) => b.blockType === "LOAD_FULL" || b.blockType === "LOAD_HALF"
    );
  }
  return { isBlocked: filtered.length > 0, blocks: filtered };
}


  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());
    // ✅ QUICK MODE: nada de fetch/blocking (igual truck/driver)
    if (quickMode) {
      setManualPlate("");
      setManualPlateError("");
      setAvailableTrailers([]);
      setLoadingTrailers(false);
      setShowPrep(false);
      setPrepStep(0);
      return;
    }
    const sid =
      mode === "single"
        ? selectedPickupDelivery?.transportsessionId ?? currentLoad?.transportsessionId ?? null
        : currentLoad?.transportsessionId ?? null;
    const noCache = { params: { _: Date.now() } };
    // ✅ relax blocks só em SINGLE (como truck/driver)
    const blockingParams =
      sid && mode === "single"
        ? { params: { _: Date.now(), originSessionId: sid } }
        : noCache;
    setLoadingTrailers(true);
    Promise.all([
      axios.get("/pickupdeliveries/trailers/with-latest-assignments", noCache),
      axios.get("/pickupdeliveries/trailer-blocking", blockingParams),
    ])
      .then(([trailersRes, blocksRes]) => {
        const blockingData = blocksRes.data || [];
        const trailersWithBlocking = (trailersRes.data || []).map((tr) => {
          const blockEntry = blockingData.find((b) => b.trailerId === tr.id);
          return { ...tr, sessionBlocks: blockEntry?.sessionBlocks || [] };
        });
        setAvailableTrailers(trailersWithBlocking);
      })
      .catch((err) => console.error("Error fetching trailer data", err))
      .finally(() => setLoadingTrailers(false));
  }, [
    isOpen,
    quickMode,
    selectedPickupDelivery?.transportsessionId,
    currentLoad?.transportsessionId,
    mode,
  ]);
  // ===== Fun loader logic (igual ao truck) =====
  useEffect(() => {
    if (!isOpen) return;
    let tickTimer = null;
    let doneTimer = null;
    if (loadingTrailers) {
      setShowPrep(true);
      setPrepStep(0);
      // 0->3 rápido, segura no 3 até acabar fetch
      let s = 0;
      tickTimer = setInterval(() => {
        s += 1;
        setPrepStep((prev) => Math.min(3, Math.max(prev, s)));
        if (s >= 3 && tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
      }, 350);
    } else {
      if (showPrep) {
        setPrepStep(4);
        doneTimer = setTimeout(() => {
          setShowPrep(false);
          setPrepStep(0);
        }, 650);
      }
    }
    return () => {
      if (tickTimer) clearInterval(tickTimer);
      if (doneTimer) clearTimeout(doneTimer);
    };
  }, [isOpen, loadingTrailers, showPrep]);
  if (!isOpen) return null;
  // --- title ---
  const providedTitle = (title ?? "").trim();
  const computedTitle = providedTitle || t("titles.trailer", { defaultValue: "Assign trailer" });
const assignedTrailerId =
  mode === "single"
    ? selectedPickupDelivery?.trailerId ?? null
    : getUniformAssignedTrailerId(
        selectedType === "pickup" ? allPickups : allDeliveries
      );
const sorted = [...availableTrailers].sort((a, b) => {
  const aAssigned =
    assignedTrailerId != null && String(a.id) === String(assignedTrailerId);
  const bAssigned =
    assignedTrailerId != null && String(b.id) === String(assignedTrailerId);
  const aConflict =
    mode === "bulk"
      ? isTrailerBlockedForBulk(a, allPickups, allDeliveries)
      : isTrailerBlockedForCurrent(a, selectedPickupDelivery);
  const bConflict =
    mode === "bulk"
      ? isTrailerBlockedForBulk(b, allPickups, allDeliveries)
      : isTrailerBlockedForCurrent(b, selectedPickupDelivery);
  const aBlocked = !!aConflict?.isBlocked;
  const bBlocked = !!bConflict?.isBlocked;
  if (aAssigned && !bAssigned) return -1;
  if (!aAssigned && bAssigned) return 1;
  if (aBlocked && !bBlocked) return 1;
  if (!aBlocked && bBlocked) return -1;
  return getTrailerLabel(a).localeCompare(getTrailerLabel(b));
});
const assignedTrailer =
  assignedTrailerId != null
    ? sorted.find((tr) => String(tr.id) === String(assignedTrailerId))
    : null;
const gridTrailers = assignedTrailer
  ? sorted.filter((tr) => String(tr.id) !== String(assignedTrailerId))
  : sorted;
  const renderBlockTitle = (block) => {
    if (!block) return "-";
    if (block.blockType === "LOAD_FULL") {
      return t("blocking.loadFull", { loadId: block.loadId ?? "?" });
    }
    if (block.blockType === "LOAD_HALF") {
      return t("blocking.loadHalf", { loadId: block.loadId ?? "?", scope: "" });
    }
    if (block.blockType === "POINT") {
      const p0 = block.points?.[0];
      return t("blocking.singleAssignment", {
        type: trPointType(p0?.type ?? "?"),
        location: p0?.location ?? "?",
      });
    }
    return t("blocking.session", { sessionId: block.transportsessionId ?? "?" });
  };

  

  const handleQuickAssign = async () => {
  const plate = String(manualPlate || "").trim().toUpperCase();

  if (!plate) {
    setManualPlateError(
      t("quickMode.errors.required", {
        defaultValue: "❌ Trailer plate is required.",
      })
    );
    return;
  }

  setManualPlateError("");

  try {
    // ✅ Delegate to parent, exactly like clicking a normal trailer card.
    // Parent already knows how to:
    // - update optimistic UI
    // - hit the correct backend endpoint
    // - refresh locations / loads
    onSelect?.({
      id: null,
      trailerPlate: plate,
      manualPlate: plate,
      isManual: true,
    });

    onClose?.();
  } catch (e) {
    console.error("❌ Quick trailer assign failed:", e);
    setManualPlateError(
      t("quickMode.errors.failed", {
        defaultValue: "❌ Failed to assign this trailer plate.",
      })
    );
  }
};



  return (
    <div className="trailer-modal-overlay">
      <div className="trailer-modal">
        <h2 className="trailer-modal-title">{computedTitle}</h2>
        <div className="trailer-modal-sub">
          {t("loadIdLabel", { defaultValue: "LOAD ID:" })} {currentLoad?.id ?? "-"}
        </div>
        {/* ===== loader premium ===== */}
        {showPrep && (
          <div className="trailer-prepbar" role="status" aria-live="polite">
            <div className="trailer-prep-top">
              <span className={`trailer-prep-chip ${prepStep >= 4 ? "done" : ""}`}>
                {prepStep >= 4
                  ? t("prep.ready", { defaultValue: "Pronto" })
                  : t("prep.preparing", { defaultValue: "A preparar" })}
              </span>
              <div className="trailer-prep-titlewrap">
                <div className="trailer-prep-title">
                  {prepStep >= 4
                    ? t("prep.sortedTitle", { defaultValue: "RESULTADOS PRONTOS" })
                    : t("prep.sortingTitle", { defaultValue: "A ORDENAR RESULTADOS" })}
                </div>
                <div className="trailer-prep-subtitle">
                  {prepStep >= 4
                    ? t("prep.sortedSub", { defaultValue: "Lista preparada para seleção." })
                    : t("prep.sortingSub", { defaultValue: "A preparar a lista para seleção..." })}
                </div>
              </div>
            </div>
            <div
              className="trailer-prep-track"
              style={{ "--pos": (prepStep / (LOADING_STEPS - 1)) * 100 }}
            >
              <div className="trailer-prep-line" />
              <div className="trailer-prep-icon" aria-hidden="true">
                <Container size={18} strokeWidth={2.2} />
              </div>
              {Array.from({ length: LOADING_STEPS }).map((_, idx) => {
                const leftPct = (idx / (LOADING_STEPS - 1)) * 100;
                return (
                  <div
                    key={idx}
                    style={{ "--x": leftPct }}
                    className={[
                      "trailer-prep-node",
                      idx < prepStep ? "done" : "",
                      idx === prepStep && prepStep < LOADING_STEPS - 1 ? "active" : "",
                      idx === LOADING_STEPS - 1 && prepStep >= LOADING_STEPS - 1
                        ? "active done"
                        : "",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                );
              })}
            </div>
            <div className="trailer-prep-below" aria-live="polite">
              <div className="trailer-prep-below-text">
                {prepStep === 0
                  ? t("prep.below.step0", { defaultValue: "A verificar disponibilidade..." })
                  : prepStep === 1
                  ? t("prep.below.step1", { defaultValue: "A carregar reboques..." })
                  : prepStep === 2
                  ? t("prep.below.step2", { defaultValue: "A cruzar conflitos..." })
                  : prepStep === 3
                  ? t("prep.below.step3", { defaultValue: "A ordenar resultados..." })
                  : t("prep.below.step4", { defaultValue: "Quase pronto..." })}
              </div>
              <div className="trailer-prep-dots" aria-hidden="true">
                <span className="trailer-prep-dot" />
                <span className="trailer-prep-dot" />
                <span className="trailer-prep-dot" />
              </div>
            </div>
          </div>
        )}
        {/* ===== selected point info (single) — só depois do loading ===== */}
{mode === "bulk" && !showPrep && (
  <div className="trailer-selected-point-info">
    <div className="trailer-point-line trailer-point-line--head">
      <span className="trailer-point-ico">
        <Navigation size={16} strokeWidth={2.2} />
      </span>
      <span className="trailer-point-meta">
        {selectedType === "pickup"
          ? t("bulk.info.pickupsLabel", {
              count: allPickups.length,
              defaultValue: `${allPickups.length} pickups`,
            })
          : t("bulk.info.deliveriesLabel", {
              count: allDeliveries.length,
              defaultValue: `${allDeliveries.length} deliveries`,
            })}
      </span>
      <span className="trailer-point-sep">•</span>
      <span className="trailer-point-ico">
        <MapPin size={16} strokeWidth={2.2} />
      </span>
      <strong className="trailer-point-strong">
        {(selectedType === "pickup"
          ? allPickups[0]?.location
          : allDeliveries[0]?.location) ||
          t("common.unnamed", { defaultValue: "Unnamed" })}
      </strong>
    </div>
    <div className="trailer-point-line trailer-point-line--meta">
      <span className="trailer-point-ico">
        <Calendar size={16} strokeWidth={2.2} />
      </span>
      <span className="trailer-point-meta">
        {(selectedType === "pickup"
          ? allPickups[0]?.date?.replaceAll("-", "/")
          : allDeliveries[0]?.date?.replaceAll("-", "/")) ||
          t("common.noDate", { defaultValue: "No date" })}
      </span>
      <span className="trailer-point-sep">•</span>
      <span className="trailer-point-ico">
        <Clock size={16} strokeWidth={2.2} />
      </span>
      <span className="trailer-point-meta">
        {(selectedType === "pickup" ? allPickups[0]?.hour : allDeliveries[0]?.hour) ||
          t("common.noHour", { defaultValue: "No hour" })}
      </span>
    </div>
  </div>
)}
{mode === "single" && selectedPickupDelivery && !showPrep && (
  <div className="trailer-selected-point-info">
    <div className="trailer-point-line trailer-point-line--head">
      <span className="trailer-point-ico">
        <Navigation size={16} strokeWidth={2.2} />
      </span>
      <strong className="trailer-point-strong">{trPointType(selectedPickupDelivery.type)}</strong>
      <span className="trailer-point-sep">•</span>
      <span className="trailer-point-ico">
        <MapPin size={16} strokeWidth={2.2} />
      </span>
      <strong className="trailer-point-strong">
        {selectedPickupDelivery.location || t("common.unnamed", { defaultValue: "Unnamed" })}
      </strong>
    </div>
    <div className="trailer-point-line trailer-point-line--meta">
      <span className="trailer-point-ico">
        <Calendar size={16} strokeWidth={2.2} />
      </span>
      <span className="trailer-point-meta">
        {selectedPickupDelivery.date || t("common.noDate", { defaultValue: "No date" })}
      </span>
      <span className="trailer-point-sep">•</span>
      <span className="trailer-point-ico">
        <Clock size={16} strokeWidth={2.2} />
      </span>
      <span className="trailer-point-meta">
        {selectedPickupDelivery.hour || t("common.noHour", { defaultValue: "No hour" })}
      </span>
    </div>
  </div>
)}
      
      {/* ===== QUICK MODE OR GRID ===== */}
      {quickMode ? (
        <div className="trailer-quick-box" onClick={(e) => e.stopPropagation()}>
          <div className="trailer-quick-head">
            <div className="trailer-quick-head-left">
              <span className="trailer-quick-head-icon">
                <Container size={20} strokeWidth={2} />
              </span>
              <div className="trailer-quick-head-texts">
                <div className="trailer-quick-head-title">
                  {t("quickMode.title", { defaultValue: "Quick trailer assignment" })}
                </div>
                <div className="trailer-quick-head-sub">
                  {t("quickMode.subtitle", {
                    defaultValue: "A subcontracted carrier is assigned to this load — using manual trailer input.",
                  })}
                </div>
              </div>
            </div>
            <span className="trailer-quick-head-badge" title="Info">i</span>
          </div>
          <div className="trailer-quick-row">
            <input
              className={`trailer-quick-input ${manualPlateError ? "input-error" : ""}`}
              value={manualPlate}
              onChange={(e) => {
                setManualPlate(e.target.value);
                if (manualPlateError) setManualPlateError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleQuickAssign();
              }}
              placeholder={t("quickMode.placeholder", { defaultValue: "Type trailer plate (e.g. L-12345)" })}
            />
          </div>
          {manualPlateError && <div className="trailer-quick-error">{manualPlateError}</div>}
          <div className="trailer-modal-actions">
            <button onClick={onClose} className="trailer-modal-btn trailer-modal-btn--secondary">
              {t("buttons.close", { defaultValue: "Close" })}
            </button>
            <button onClick={handleQuickAssign} className="trailer-modal-btn trailer-modal-btn--primary">
              {t("buttons.assign", { defaultValue: "Assign" })}
            </button>
          </div>
        </div>
      ) : (
        !showPrep && (
          <div className="trailer-grid">
            {assignedTrailer && (
              <div className="trailer-selected-banner">
                <div className="trailer-banner-left">
                  <span className="trailer-banner-icon">
                    <Container size={26} strokeWidth={2.2} />
                  </span>
                  <div className="trailer-banner-texts">
                    <div className="trailer-banner-plate">{getTrailerLabel(assignedTrailer)}</div>
                    <div className="trailer-banner-sub">
                      {t("banner.alreadyAssigned", { defaultValue: "Already assigned" })}
                    </div>
                  </div>
                </div>
                <div className="trailer-banner-right">
                  <span className="trailer-banner-check">✓</span>
                </div>
              </div>
            )}
{gridTrailers.map((tr) => {
  const conflict =
    mode === "bulk"
      ? isTrailerBlockedForBulk(tr, allPickups, allDeliveries)
      : isTrailerBlockedForCurrent(tr, selectedPickupDelivery);
  const isBlocked = !!conflict?.isBlocked;
  const isExpanded = expanded.has(tr.id);
  const label = getTrailerLabel(tr);
  const isAssigned =
    assignedTrailerId != null && String(tr.id) === String(assignedTrailerId);
              const statusText = isAssigned
                ? t("status.alreadyAssigned", { defaultValue: "Already assigned" })
                : isBlocked
                ? t("status.blockedBy", { defaultValue: "Blocked" })
                : t("status.available", { defaultValue: "Available" });
              return (
                <div className="trailer-grid-item" key={tr.id}>
                  <div
                    className={`trailer-card ${isBlocked ? "blocked" : ""}`}
                    onClick={() => {
                      if (isBlocked) return;
                      onSelect?.(tr);
                      onClose?.();
                    }}
                  >
                    <div className="trailer-card-top">
                      <div className="trailer-left">
                        <span className="trailer-icon">
                          <Container size={20} strokeWidth={2} />
                        </span>
                        <span className="trailer-plate">{label}</span>
                        <span className={`trailer-dot ${isBlocked ? "red" : "green"}`} />
                      </div>
                      {isBlocked && (
                        <button
                          className={`trailer-caret ${isExpanded ? "open" : ""}`}
                          onClick={(e) => toggleExpand(tr.id, e)}
                          aria-label={t("aria.toggleBlockDetails", { defaultValue: "Toggle block details" })}
                        />
                      )}
                    </div>
                    <div className={`trailer-status ${isBlocked ? "blocked" : "ok"}`}>
                      {statusText}
                    </div>
{isBlocked && (
  <div
    className={`trailer-block-details ${isExpanded ? "show" : ""}`}
    onClick={(e) => e.stopPropagation()}
  >
    {/* SINGLE */}
    {mode === "single" && conflict?.block && (
      <div className="trailer-blocked-msg">
        <span className="trailer-block-line">
          <span className="trailer-block-ico">
            <Ban size={16} strokeWidth={2.2} />
          </span>
          <span className="trailer-block-text">
            {renderBlockTitle(conflict.block)}
          </span>
        </span>
        <div className="trailer-blocked-interval">
          <span className="trailer-block-ico">
            <Timer size={16} strokeWidth={2.2} />
          </span>
          {fmtDateTime(conflict.block.start)}{" "}
          {t("common.arrow", { defaultValue: "→" })}{" "}
          {fmtDateTime(conflict.block.end)}
        </div>
        {conflict.block.points?.length > 0 && (
          <div className="trailer-caused-by">
            {conflict.block.points.slice(0, 5).map((p, i) => (
              <div key={i} className="trailer-caused-by-row">
                <span className="trailer-block-ico">
                  <AlertTriangle size={16} strokeWidth={2.2} />
                </span>
                <span>
                  {trPointType(p.type)} {t("common.at", { defaultValue: "at" })}{" "}
                  <strong>{p.location || "-"}</strong> — {p.date} {p.hour}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
    {/* BULK */}
    {mode === "bulk" && conflict?.blocks?.length > 0 && (
      <div className="trailer-blocked-msg">
        {conflict.blocks.map((blk, idx) => (
          <div key={idx} className="bulk-block">
            •{" "}
            {blk.blockType === "FULL_SESSION"
              ? t("blocking.session", { sessionId: blk.sessionId })
              : blk.blockType === "LOAD_FULL"
              ? t("blocking.loadFull", { loadId: blk.loadId ?? "?" })
              : blk.blockType === "LOAD_HALF"
              ? (() => {
                  const firstType = blk.points?.[0]?.type;
                  const scope =
                    String(firstType).toUpperCase() === "PICKUP"
                      ? t("blocking.allPickups", { defaultValue: "all pickups" })
                      : t("blocking.allDeliveries", { defaultValue: "all deliveries" });
                  return t("blocking.loadHalf", {
                    loadId: blk.loadId ?? "?",
                    scope,
                  });
                })()
              : blk.blockType === "POINT"
              ? t("blocking.singleAssignment", {
                  type: trPointType(blk.points?.[0]?.type ?? "?"),
                  location: blk.points?.[0]?.location ?? "?",
                })
              : "?"}
            <div className="trailer-blocked-interval">
              ⏱ {fmtDateTime(blk.start)} {t("common.arrow", { defaultValue: "→" })}{" "}
              {fmtDateTime(blk.end)}
            </div>
            {blk.causedBy?.length > 0 && (
              <div className="trailer-caused-by">
                {blk.causedBy.map((c, i) => (
                  <div key={i} className="trailer-caused-by-row">
                    <span className="trailer-block-ico">
                      <AlertTriangle size={16} strokeWidth={2.2} />
                    </span>
                    <span>
                      {t("blocking.causedBy", { defaultValue: "Caused by" })}{" "}
                      {trPointType(c.type)} {t("common.at", { defaultValue: "at" })}{" "}
                      <strong>{c.location || "-"}</strong> — {c.date} {c.hour}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {blk.points?.length > 0 && (
              <div className="trailer-caused-by">
                {blk.points.map((p, i) => (
                  <div key={i} className="trailer-caused-by-row">
                    <span className="trailer-block-ico">
                      <AlertTriangle size={16} strokeWidth={2.2} />
                    </span>
                    <span>
                      {trPointType(p.type)} {t("common.at", { defaultValue: "at" })}{" "}
                      <strong>{p.location || "-"}</strong> — {p.date} {p.hour}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {/* ✅ Global close button only when NOT quick mode */}
        {!quickMode && (
          <button onClick={onClose} className="trailer-modal-close">
            {t("buttons.close", { defaultValue: "Close" })}
          </button>
        )}
      </div>
    </div>
  );
}
export default TrailerSelectorModal;
